import { get } from 'lodash';

import { mcodeVer } from './McodeVersion';

// API видео-плеера https://github.yandex-team.ru/IMS/ad-sdk-js/blob/master/BANNER_AD.md
export interface CanvasPcodeVideoFrameParams {
    vast: string;
    autostartoff: boolean;
    soundOnStart: boolean;
    keepVideoOnEnd: boolean;
    keepMuteOnUserAction: boolean;
    unmuteInvitation: boolean;
    switchVolumeOnRollOver: boolean;
    withPartnerIdAndCategory: boolean;
    showGuiForVpaid: boolean;
    numruns: number;
    lang: string;
    mcodever?: number;
}

type InitCallbacks = {
    onAdEnd: () => void;
    onInited: () => void;
};

interface PcodeVideoController {
    play: () => void;
    pause: () => void;
    destroy: () => void;
    onAdEnd: () => void;
}

export class PcodeVideoLoader {
    private static loadVideoPromise?: Promise<void>;

    static loadScript(): Promise<void> {
        if (!PcodeVideoLoader.loadVideoPromise) {
            PcodeVideoLoader.loadVideoPromise = new Promise((resolve): void => {
                const script = document.createElement('script');

                script.src = 'https://yastatic.net/awaps-ad-sdk-js/1_0/banner_ad.min.js';

                script.onload = (): void => resolve();

                document.head.appendChild(script);
            });
        }

        return PcodeVideoLoader.loadVideoPromise;
    }
}

export class PcodeVideoRendererCanvas {
    private renderNode: HTMLDivElement;
    private playerController: PcodeVideoController;

    private onAdEnd: () => void;
    private onInited: () => void;

    private isLoadingInProgress: boolean = false;
    private isLoadingIsFinished: boolean = false;
    // нужно в дополнение к autostartoff=false для обработки кейса, когда в процессе инициализации плеера
    // поступила извне команда на его старт
    private startPlayerAfterInit: boolean = false;

    private nextData: Partial<CanvasPcodeVideoFrameParams>;

    constructor(renderNode: HTMLDivElement, callbacks: InitCallbacks) {
        this.renderNode = renderNode;
        this.onAdEnd = callbacks.onAdEnd;
        this.onInited = callbacks.onInited;
    }

    public play(): void {
        if (this.isLoadingIsFinished) {
            this.playerController.play();
        } else {
            this.startPlayerAfterInit = true;
        }
    }

    public pause(): void {
        if (this.isLoadingIsFinished) {
            this.playerController.pause();
        }
    }

    public destroy(): void {
        if (this.isLoadingIsFinished) {
            this.playerController.destroy();
        }
    }

    public init(data: Partial<CanvasPcodeVideoFrameParams>): void {
        if (this.isLoadingInProgress) {
            this.nextData = data;

            return;
        }

        this.initPcodeLoader(data);
    }

    private initPcodeLoader(data: Partial<CanvasPcodeVideoFrameParams>): void {
        this.isLoadingInProgress = true;
        PcodeVideoLoader.loadScript().then(() => {
            // если пока скрипт грузился упели прийти новые данные для pcode
            // то инициализируем pcode уже с новыми данными
            this.initVideoPlayer(this.nextData || data);

            delete this.nextData;
        });
    }

    private initVideoPlayer(data: Partial<CanvasPcodeVideoFrameParams>): void {
        if (this.renderNode && this.renderNode.firstChild) {
            while (this.renderNode.firstChild) {
                this.renderNode.firstChild.remove();
            }
        }

        const bannerAdAPI = get(window, 'ya.mediaAd.bannerAd');

        if (!bannerAdAPI) {
            throw new Error('ya.mediaAd.bannerAd is not defined');
        }
        bannerAdAPI.initVideo(
            this.renderNode,
            this.buildPcodeIframeData(data),
            (error: { message?: string }, Controller: PcodeVideoController) => {
                if (error) {
                    throw error;
                }
                if (this.nextData) {
                    this.initVideoPlayer(this.nextData);
                    delete this.nextData;
                } else {
                    this.playerController = Controller;

                    this.playerController.onAdEnd = this.onAdEnd;
                    this.isLoadingInProgress = false;
                    this.isLoadingIsFinished = true;

                    this.onInited();
                    if (this.startPlayerAfterInit) {
                        // из-за асинхронности в pcode без таймаута play не стартанет
                        setTimeout(() => {
                            this.playerController.play();
                            this.startPlayerAfterInit = false;
                        }, 0);
                    }
                }
            }
        );
    }

    private buildPcodeIframeData(data: Partial<CanvasPcodeVideoFrameParams>): CanvasPcodeVideoFrameParams {
        const { vast = '', lang = 'ru', autostartoff = true, soundOnStart = true } = data;

        return {
            vast,
            autostartoff,
            soundOnStart,
            keepVideoOnEnd: true,
            keepMuteOnUserAction: true,
            unmuteInvitation: false,
            switchVolumeOnRollOver: false,
            withPartnerIdAndCategory: false,
            showGuiForVpaid: true,
            numruns: 0,
            lang,
            mcodever: mcodeVer
        };
    }
}
